import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePlan } from "@/hooks/use-plan";
import { track } from "@/lib/analytics/analytics";
import { planFor, type PlanFeature } from "@/lib/plans/plans";
import { onUpgradeRequest } from "@/lib/plans/upgrade";

/**
 * The upgrade prompt, mounted once in the layout. Opens on requestUpgrade() (lib/plans/upgrade.ts):
 * from buttons at a limit, and for every request the server refuses because of the plan.
 */
export function UpgradeDialog() {
  const { t } = useTranslation();
  const { plan, limits } = usePlan();
  const [feature, setFeature] = useState<PlanFeature | null>(null);

  useEffect(
    () =>
      onUpgradeRequest((requested) => {
        setFeature(requested);
        track("upgrade_prompt_shown", { feature: requested });
      }),
    [],
  );

  const target = feature ? planFor(feature, plan) : "pro";
  const n =
    feature === "invoices"
      ? limits.invoicesPerMonth
      : feature === "customers"
        ? limits.customers
        : feature === "paymentMethods"
          ? limits.paymentMethods
          : feature === "assistant"
            ? limits.assistantPerDay
            : null;

  return (
    <Dialog open={feature !== null} onOpenChange={(open) => !open && setFeature(null)}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-dialog">
        {feature && (
          <>
            <DialogHeader>
              <div className="mb-2 flex size-10 items-center justify-center rounded-full bg-brand/10 text-brand">
                <Sparkles className="size-5" aria-hidden />
              </div>
              <DialogTitle>{t(`upgrade.features.${feature}.title`)}</DialogTitle>
              <DialogDescription>
                {t(`upgrade.features.${feature}.description`, { n: n ?? 0 })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setFeature(null)}>
                {t("upgrade.later")}
              </Button>
              <Button asChild variant="brand" onClick={() => track("upgrade_clicked", { feature })}>
                <Link
                  to={`/settings/plans?highlight=${target}`}
                  onClick={() => setFeature(null)}
                  data-testid="upgrade-dialog-plans"
                >
                  {t("upgrade.upgradeTo", { plan: t(`plans.names.${target}`) })}
                </Link>
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
